import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames/bind';
import { useLocation, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';

import Breadcrumbs from '../../Breadcrumbs';
import DcscPoweredByWithTooltip from '../../Shared/DcscPoweredByWithTooltip';
import Tabs from '../../Shared/Tabs';
import PeriodFilter from '../../Shared/PeriodFilter';
import DataTableCollapsable from '../../Shared/DataTableCollapsable';
import Preloader from '../../Preloader';
import PerPage from '../PremiumPublications/Pagination/PerPage';
import TrendChart from '../TrendChart';

import DCSCSectorsSvc from '../../../services/dbServices/DCSCSectorsSvc';
import SentimentsSvc from '../../../services/dbServices/SentimentsSvc';

import withMetatag from '../../../decorators/metadataDecorators/metatagsTitleDecorator';

import SECTORS_TABLE_COLUMNS from '../../../data/directory/dcsc-sectors/sectorsTableColumns';
import { DCSC_SECTOR_BREADCRUMBS } from '../../../data/directory/constants';
import sentimentTimeFilter from '../../../data/directory/dcsc-sectors/sentimentTimeFilter';

import convertPermalinkToPath from '../../../helpers/urlHelpers';
import { getAverageSentimentStoryText } from '../../../helpers/sentimentHelpers';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const DCSCSectors = () => {
  const { hash, pathname } = useLocation();
  const history = useHistory();
  const [pageSize, setPageSize] = useState('15');
  const [sentimentPeriod, setSentimentPeriod] = useState('1m');
  const [page, setPage] = useState(1);
  const [activeLevel, setActiveLevel] = useState(() => {
    const hashLevel = hash.slice(1).toLowerCase();
    if (
      hashLevel === 'level1'
      || hashLevel === 'level2'
      || hashLevel === 'level3'
      || hashLevel === 'level4'
    ) {
      return hashLevel;
    }

    return 'level1';
  });
  const [prefix, setPrefix] = useState(undefined);
  const [
    {
      levels,
      paginationAlphabetHeader,
      totalPages,
      isLoading,
    },
    setData,
  ] = useState({
    levels: [],
    paginationAlphabetHeader: [],
    totalPages: 0,
    isLoading: true,
  });

  const levelTabs = useMemo(() => (
    [
      {
        value: 'level1',
        label: 'Level 1',
      },
      {
        value: 'level2',
        label: 'Level 2',
      },
      {
        value: 'level3',
        label: 'Level 3',
      },
      {
        value: 'level4',
        label: 'Level 4',
      },
    ]
  ), []);

  const tableData = useMemo(() => (
    levels.map((level) => {
      const sentimentValue = (
        (level.sentimentData?.sentiment || level.sentimentData?.sentiment === 0)
          ? getAverageSentimentStoryText(level.sentimentData.sentiment, false, true)
          : null
      );

      return {
        uid: level.id,
        mainData: {
          name: {
            url: convertPermalinkToPath(level.permalink),
            value: level.name,
          },
          sentiment: sentimentValue && (
            <div className={cx('sentiment', sentimentValue.type)}>
              <FontAwesomeIcon icon={sentimentValue.icon} />
              &nbsp;&nbsp;
              {sentimentValue.value > 0 ? `+${sentimentValue.value}` : sentimentValue.value}
              %
            </div>
          ),
          sentimentChart: level.sentimentData?.diagram?.sentiments?.some(Boolean) && (
            <div className={Styles['sentiment-chart']}>
              <TrendChart
                data={(
                  level.sentimentData?.diagram?.sentiments.map((sentiment, index) => ({
                    xData: index,
                    yData: sentiment ?? 0,
                  }))
                )}
                width="178"
                height="15"
              />
            </div>
          ),
        },
      };
    })
  ), [levels]);

  const onLevelChange = useCallback((level) => {
    setPage(1);
    setPrefix(undefined);
    setActiveLevel(level);
    history.replace(`${pathname}#${level}`);
  }, [history]);

  const onLetterChange = useCallback((letter) => {
    setPage(1);
    setPrefix(letter);
  }, []);

  useEffect(() => {
    let mounted = true;

    setData((current) => ({
      levels: current.levels,
      paginationAlphabetHeader: current.paginationAlphabetHeader,
      totalPages: current.totalPages,
      isLoading: true,
    }));

    DCSCSectorsSvc.getLevelTopics(
      activeLevel,
      {
        per_page: Number(pageSize),
        page,
        paginationAlphabetHeader: activeLevel === 'level4',
        prefix,
      },
    ).then(({ levels, totalPages, paginationAlphabetHeader }) => {
      if (!mounted) {
        return;
      }

      setData({
        levels,
        paginationAlphabetHeader: paginationAlphabetHeader ?? [],
        totalPages,
        isLoading: false,
      });
    });

    return () => {
      mounted = false;
    };
  }, [activeLevel, page, pageSize, prefix]);

  useEffect(() => {
    if (isLoading || !levels.length) {
      return;
    }

    let mounted = true;
    SentimentsSvc.getSentimentsOfEntities({
      entities: levels,
      period: sentimentPeriod,
    }).then((sentiments) => {
      if (!mounted) {
        return;
      }

      const sentimentsMap = new Map();
      sentiments.forEach((sentiment) => {
        sentimentsMap.set(`${sentiment.entityType}:${sentiment.entityId}`, sentiment);
      });

      setData((current) => ({
        ...current,
        levels: current.levels.map((level) => ({
          ...level,
          sentimentData: sentimentsMap.get(`${level.type}:${level.id}`),
        })),
      }));
    });

    return () => {
      mounted = false;
    };
  }, [isLoading, sentimentPeriod]);

  return (
    <section className={Styles['dcsc-sectors']}>
      <Breadcrumbs items={DCSC_SECTOR_BREADCRUMBS} />
      <div className={Styles.header}>
        <h1 className={Styles.title}>Full list of sectors</h1>
        <DcscPoweredByWithTooltip className={Styles['dcsc-logo']} />
      </div>
      <div className={Styles.filters}>
        <Tabs
          tabs={levelTabs}
          value={activeLevel}
          onChange={onLevelChange}
          className={Styles.tabs}
        />
        <PeriodFilter
          initialFilters={sentimentTimeFilter}
          label="Period:"
          onStoryFilterChange={setSentimentPeriod}
        />
      </div>
      {!!paginationAlphabetHeader.length && (
      <>
        <div className={cx('alphabet-pagination')}>
          <div onClick={() => onLetterChange(undefined)} className={cx('alphabet-pagination-letter', { selected: !prefix })}>
            ALL
          </div>
          {paginationAlphabetHeader.map((letter) => (
            <div
              key={letter}
              onClick={() => onLetterChange(letter)}
              className={cx('alphabet-pagination-letter', { selected: prefix === letter })}
            >
              {letter}
            </div>
          ))}
        </div>
        <div className={cx('alphabet-pagination-mobile-wrapper')}>
          <select
            onChange={(e) => onLetterChange(e.target.value || undefined)}
            className={cx('alphabet-pagination-mobile')}
            required
          >
            <option value="">
              All
            </option>
            {paginationAlphabetHeader.map((letter) => (
              <option value={letter} key={letter}>
                {letter}
              </option>
            ))}
          </select>
          <FontAwesomeIcon
            className={Styles['alphabet-pagination-mobile-icon']}
            icon={faChevronDown}
          />
        </div>
      </>
      )}
      <Preloader loading={isLoading && !levels.length} />
      {!!levels.length && (
      <>
        <DataTableCollapsable
          columns={SECTORS_TABLE_COLUMNS}
          data={tableData}
          rowClassName={Styles['table-row']}
        />
        <div className={Styles.pagination}>
          <PerPage
            selectedValue={pageSize}
            setSelectedValue={setPageSize}
            rounded
          />
          <div className={Styles['pagination-buttons']}>
            <span className={Styles.label}>Page:</span>
            <button
              disabled={page === 1}
              type="button"
              className={`${Styles.button} ${Styles.prev}`}
              onClick={() => setPage((page) => page - 1)}
            >
              <FontAwesomeIcon className={Styles.icon} icon={faChevronLeft} />
              Prev.
            </button>
            <button
              disabled={page === totalPages}
              type="button"
              className={`${Styles.button} ${Styles.next}`}
              onClick={() => setPage((page) => page + 1)}
            >
              Next
              <FontAwesomeIcon className={Styles.icon} icon={faChevronRight} />
            </button>
          </div>
        </div>
      </>
      )}
    </section>
  );
};

const DCSCSectorsMemorized = memo(DCSCSectors);
DCSCSectorsMemorized.displayName = 'DCSCSectors';

export default withMetatag(DCSCSectorsMemorized, 'news/directory/dcsc-sectors');
